import { ControlContext } from 'provider/ControlProvider';
import { Fragment, useState, useContext } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { LOGIN_USER } from 'graphql/auth';
import { Credentials } from 'types/global';
import Input from 'components/Input';
import CommonButton from 'components/CommonButton';
import PasswordInput from 'components/PasswordInput';
import PulseLoader from 'react-spinners/PulseLoader';
import { LoginSchema } from 'constant/constant';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function LoginForm() {
  const { showModal, setShowModal, setToken } = useContext(ControlContext);
  const [loading, setLoading] = useState(false);
  function closeModal() {
    setShowModal(0);
  }
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Credentials>({
    mode: 'onChange',
    resolver: yupResolver(LoginSchema),
  });
  const [loginCustomer] = useMutation(LOGIN_USER);

  const alert = (content: string) =>
    toast.error(content, {
      position: 'bottom-left',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'dark',
    });
  const success = (content: string) =>
    toast.success(content, {
      position: 'bottom-left',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'dark',
    });

  const onSubmit: SubmitHandler<Credentials> = async ({ email, password }) => {
    setLoading(true);
    loginCustomer({
      variables: {
        email: email ?? '',
        password: password ?? '',
      },
    })
      .then((response) => {
        if (
          response.data.customerAccessTokenCreate.customerAccessToken !== null
        ) {
          success(`Welcome to Spore's World`);
          localStorage.setItem(
            'accessToken',
            response.data.customerAccessTokenCreate.customerAccessToken
              .accessToken
          );
          setToken(
            response.data.customerAccessTokenCreate.customerAccessToken
              .accessToken
          );
        } else {
          alert('The email or password is not correct!');
        }
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        alert(error.message);
      });
  };
  return (
    <Transition appear show={showModal === 2} as={Fragment}>
      <Dialog as='div' className='relative z-10' onClose={closeModal}>
        <ToastContainer />
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-black bg-opacity-25' />
        </Transition.Child>

        <div className='fixed inset-0 overflow-y-auto'>
          <div className='flex min-h-full items-center justify-center text-center'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 scale-95'
              enterTo='opacity-100 scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 scale-100'
              leaveTo='opacity-0 scale-95'
            >
              <Dialog.Panel className='w-full max-w-md transform  text-left align-middle transition-all'>
                <img
                  src='/assets/bg.png'
                  className='absolute -top-2 md:-top-12 object-cover'
                  alt='Spore&aposs Login Form'
                />
                <div className='py-9 px-4 sm:rounded-lg sm:px-9 relative'>
                  <div className='flex justify-center mb-7'>
                    <p className='text-2xl text-zinc-700 font-bold'>Sign In</p>
                  </div>
                  <form
                    className='space-y-6  mx-16'
                    onSubmit={handleSubmit(onSubmit)}
                  >
                    <Input
                      id='email'
                      type='email'
                      errors={errors.email}
                      title='Email Address'
                      {...register('email')}
                    />

                    <PasswordInput
                      id='password'
                      errors={errors.password}
                      title='Password'
                      {...register('password')}
                    />
                    <div className='flex justify-between items-center text-zinc-900 text-xs'>
                      <div className='flex items-center'>
                        <div>
                          <input type='checkbox' id='my-checkbox' />
                          <label htmlFor='my-checkbox' />
                        </div>

                        <label
                          htmlFor='remember-me'
                          className='ml-2 block hover:text-zinc-600 '
                        >
                          Remember me
                        </label>
                      </div>
                      <button
                        className='hover:text-zinc-600'
                        onClick={(e) => {
                          e.preventDefault();
                          setShowModal(3);
                        }}
                      >
                        Forget Password?
                      </button>
                    </div>
                    <div className='flex justify-center'>
                      <CommonButton
                        type='submit'
                        className='w-1/2'
                        active={!loading}
                      >
                        {loading ? (
                          <div className='flex justify-center items-center space-x-4'>
                            <p>Sign In</p>
                            <PulseLoader size={6} color='#72A0F7' />
                          </div>
                        ) : (
                          <p>Sign In</p>
                        )}
                      </CommonButton>
                    </div>
                  </form>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
